import * as groupBy from 'lodash/groupBy'
import { Reducer, Collection } from 'interfaces'
import { InsightDependency, InsightsCollection, InsightsDataState, PlacementType, Insight, PlacementOf, SocialInsightsCollection } from 'interfaces/insights'
import { FAIL_TOGGLE_DISMISS_INSIGHT, SET_INSIGHTS, TOGGLE_DISMISS_INSIGHT, UNDISMISS_INSIGHTS_BY_PLACEMENT } from 'actions/insightsActions'
import { getItemsFromCollection, normalize, setItemInCollection, createEmptyCollection } from 'helpers/normalizeHelpers'
import { INSIGHT_COMMON_REFRESH_TRIGGERS, INSIGHT_REFRESH_TRIGGERS_BY_DEPENDENCY, needDiscardData } from 'helpers/discardTriggerHelpers'
import { isRequestAction, isSuccessAction } from 'helpers/actionsHelpers'

import { createInsightGroupKey } from 'helpers/insightHelpers'
import { INSIGHT_PLACEMENT_DEPENDENCIES } from 'constants/insights'
import { getStaleableData } from 'helpers/storeHelpers'
import { Staleable } from 'interfaces/global'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { GET_SOCIAL_INSIGHTS } from 'actions/socialInsightsActions'

const proccessInsightsPayload = <TInsight extends Insight | SocialInnerInsight>(
  insights: Collection<TInsight, 'id'>, placements: PlacementOf<TInsight>[], prevState: InsightsDataState
) => {
  const allInsights = getItemsFromCollection<TInsight, 'id'>(insights)
  const insightsByPlacement: Record<PlacementOf<TInsight>, TInsight[]> = groupBy(allInsights, ({ placement }) => placement) as any

  const nextState = placements.reduce<Exclude<InsightsDataState, null>>(
    (acc, placement) => {
      const key = createInsightGroupKey(placement)
      const nextPlacementCollection = insightsByPlacement[placement]
        ? normalize(insightsByPlacement[placement] || [], 'id')
        : createEmptyCollection<TInsight, 'id'>()

      const nextStaleable: Staleable<Collection<TInsight, 'id'>> = {
        data: nextPlacementCollection,
        isStale: false,
      }

      return {
        ...acc,
        [key]: nextStaleable,
      }
    },
    prevState ? { ...prevState } : {},
  )

  return nextState
}

const insightsDataReducer: Reducer<InsightsDataState> = (prevState = null, action) => {
  if (prevState && needDiscardData(INSIGHT_COMMON_REFRESH_TRIGGERS)(action)) {
    return Object.keys(prevState).reduce((newState, key: PlacementType) => {
      newState[key] = {
        ...prevState[key] as Staleable<InsightsCollection & SocialInsightsCollection>,
        isStale: true,
      }

      return newState
    }, {} as NonNullable<InsightsDataState>)
  }

  if (prevState) {
    let isStale = false
    const staleState = { ...prevState }
    Object.keys(INSIGHT_REFRESH_TRIGGERS_BY_DEPENDENCY).forEach((dependency: InsightDependency) => {
      if (needDiscardData(INSIGHT_REFRESH_TRIGGERS_BY_DEPENDENCY[dependency])(action)) {
        INSIGHT_PLACEMENT_DEPENDENCIES[dependency].forEach((placement) => {
          const key = createInsightGroupKey(placement)
          if (prevState[key]) {
            (staleState[key] as Staleable<InsightsCollection>).isStale = true
            isStale = true
          }
        })
      }
    })

    if (isStale) {
      return staleState
    }
  }

  switch (action.type) {
    case SET_INSIGHTS: {
      const nextState = proccessInsightsPayload<Insight>(
        action.payload.insights,
        action.payload.placements,
        prevState
      )

      return nextState
    }

    case GET_SOCIAL_INSIGHTS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { response } = action.payload

      const nextState = proccessInsightsPayload<SocialInnerInsight>(
        response.insights,
        response.placements,
        prevState
      )

      return nextState
    }

    case UNDISMISS_INSIGHTS_BY_PLACEMENT: {
      if (!prevState || !isRequestAction(action)) {
        return prevState
      }

      const { placement } = action.payload.params
      const key = createInsightGroupKey(placement)

      const data = getStaleableData(prevState[key])
      if (!data) {
        return prevState
      }

      const { allKeys, byKey } = data
      const undismissedIds = action.payload.params.insights.map((insight) => insight.id)
      const undismissedByKey = undismissedIds.reduce((result, id) => {
        result[id] = {
          ...byKey[id],
          is_dismissed: false,
        }
        return result
      }, {} as typeof byKey)

      return {
        ...prevState,
        [key]: {
          ...prevState[key],
          data: {
            allKeys,
            byKey: {
              ...byKey,
              ...undismissedByKey,
            },
          },
        },
      }
    }

    case TOGGLE_DISMISS_INSIGHT: {
      if (!prevState) {
        return prevState
      }

      const { placement } = action.payload.insight
      const key = createInsightGroupKey(placement)
      const data = getStaleableData(prevState[key])

      if (!data) {
        return prevState
      }

      const { is_dismissed, insight } = action.payload
      const nextInsight = {
        ...data.byKey[insight.id],
        is_dismissed,
        is_new: false,
      }
      const nextData = setItemInCollection(data, nextInsight, 'id')

      return {
        ...prevState,
        [key]: {
          ...prevState[key],
          data: nextData,
        },
      }
    }

    case FAIL_TOGGLE_DISMISS_INSIGHT: {
      if (!prevState) {
        return prevState
      }

      const { insight } = action.payload
      const { placement } = insight
      const key = createInsightGroupKey(placement)
      const data = getStaleableData(prevState[key])
      const nextData = setItemInCollection(data, insight, 'id')

      return {
        ...prevState,
        [key]: {
          ...prevState[key],
          data: nextData,
        },
      }
    }

    default:
      return prevState
  }
}

export default insightsDataReducer
